import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const WeatherHourSchema = z
    .object({
        etc: z.number().nullish(),
        hour: z.string().trim(),
        humidity: z.number(),
        icon: z.string().trim(),
        rainProb: z.number(),
        rainFall: z.number(),
        temperature: z.number(),
        windDirection: z.string().trim(),
        windSpeedFrom: z.number(),
        windSpeedTo: z.number(),
    })
    .brand<"WeatherHour">();

export type WeatherHourInputProps = z.input<typeof WeatherHourSchema>;
type WeatherHourProps = z.output<typeof WeatherHourSchema>;

export class WeatherHour {
    private constructor(private readonly props: Readonly<WeatherHourProps>) {}

    get etc(): Nullable<number> | undefined {
        return this.props.etc;
    }

    get hour(): string {
        return this.props.hour;
    }

    get humidity(): number {
        return this.props.humidity;
    }

    get icon(): string {
        return this.props.icon;
    }

    get rainProb(): number {
        return this.props.rainProb;
    }

    get rainFall(): number {
        return this.props.rainFall;
    }

    get temperature(): number {
        return this.props.temperature;
    }

    get windDirection(): string {
        return this.props.windDirection;
    }

    get windSpeedFrom(): number {
        return this.props.windSpeedFrom;
    }

    get windSpeedTo(): number {
        return this.props.windSpeedTo;
    }

    static validate(
        props: Readonly<MaybeValidProps<WeatherHourInputProps>>,
    ): Result<WeatherHourProps, z.ZodError<WeatherHourProps>> {
        const result = WeatherHourSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<WeatherHourInputProps>,
    ): Result<WeatherHour, z.ZodError<WeatherHourProps>> {
        return WeatherHour.validate(props).map(
            (validatedProps) => new WeatherHour(validatedProps),
        );
    }

    toCreateProps(): WeatherHourInputProps {
        return {
            etc: this.props.etc,
            hour: this.props.hour,
            humidity: this.props.humidity,
            icon: this.props.icon,
            rainProb: this.props.rainProb,
            rainFall: this.props.rainFall,
            temperature: this.props.temperature,
            windDirection: this.props.windDirection,
            windSpeedFrom: this.props.windSpeedFrom,
            windSpeedTo: this.props.windSpeedTo,
        };
    }
}

const WeatherHoursSchema = z
    .object({
        weatherDays: z.array(
            z.custom<WeatherHour>((val) => val instanceof WeatherHour, {
                error: "Invalid WeatherHour instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"WeatherHours">();

export type WeatherHoursInputProps = z.input<typeof WeatherHoursSchema>;
export type WeatherHoursProps = z.output<typeof WeatherHoursSchema>;

export class WeatherHours {
    private constructor(private readonly props: Readonly<WeatherHoursProps>) {}

    get weatherDays(): readonly Readonly<WeatherHour>[] {
        return this.props.weatherDays;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): WeatherHours {
        return WeatherHours.create({
            weatherDays: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<WeatherHoursInputProps>>,
    ): Result<WeatherHoursProps, z.ZodError<WeatherHoursProps>> {
        const result = WeatherHoursSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<WeatherHoursInputProps>,
    ): Result<WeatherHours, z.ZodError<WeatherHoursProps>> {
        return WeatherHours.validate(props).map(
            (validatedProps) => new WeatherHours(validatedProps),
        );
    }
}
